import { Grid, Typography, Box, GridSize, Tooltip } from '@mui/material';
import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { FormGridInput, LabelBox } from './FormGrid.styles';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';

interface IFormGridItem {
  name?: string;
  label: ReactNode;
  id: string;
  value?: string | number | Record<string, string | number>;
  required?: boolean;
  disabled?: boolean;
  cellSize?: GridSize;
  onSetFieldValue?: (value: string | number) => void;
  validate?: (value: string) => string | undefined;
  inputProps?: any;
  values?: Record<string, string | number>; // for controlled components
}
interface IPlanFormGrid {
  formName: string;
  formDescription?: string;
  fields: IFormGridItem[];
  initialValues?: IFieldValues;
  values?: IFieldValues; // controlled values prop
  onChangeFieldValues?: (values: IFieldValues) => void;
}

interface IFormItemLabel {
  label?: ReactNode;
  description?: string | false;
}

export interface IFieldValues {
  kpi?: Record<string, string | number>;
  [key: string]: string | number | Record<string, string | number> | undefined;
}

export const FormItemLabel = ({
  label,
  description,
  required,
}: IFormItemLabel & { required?: boolean }) => {
  return (
    label && (
      <LabelBox>
        {label}
        {required && <span style={{ color: 'red', marginLeft: 2 }}>*</span>}
        {description !== false && (
          <Tooltip title={description || ''} arrow placement={'top'}>
            <InfoIcon />
          </Tooltip>
        )}
      </LabelBox>
    )
  );
};

const FormGridItem = ({
  cellSize = 6,
  label,
  value,
  onSetFieldValue,
  name,
  validate,
  inputProps,
  required,
  ...props
}: IFormGridItem) => {
  const { t } = useTranslation();
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [currentValue, setCurrentValue] = useState(value || '');

  useEffect(() => setCurrentValue((prev) => (prev !== value ? value : prev)), [value]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    // Only allow numbers, decimal points, and negative signs
    const numericValue = newValue.replace(/[^0-9.-]/g, '');
    setCurrentValue(numericValue);
    if (validate) setErrorMsg(validate(numericValue));
    else if (required) setErrorMsg(numericValue === '' ? t('requiredField') : undefined);
    else setErrorMsg(undefined);
  };

  return (
    <Grid item xs={12} sm={cellSize}>
      <FormGridInput
        name={name || props.id}
        {...props}
        {...inputProps}
        value={
          currentValue === undefined || currentValue === null || Number.isNaN(currentValue)
            ? ''
            : currentValue
        }
        error={!!errorMsg}
        onChange={handleChange}
        onKeyDown={(e) => {
          // Allow control keys (copy, paste, select all, etc.)
          if (e.ctrlKey || e.metaKey || e.altKey) return;

          // Prevent non-numeric characters from being entered
          // Allow: numbers, decimal point, minus sign, backspace, delete, arrow keys, tab
          const allowedKeys = [
            '0',
            '1',
            '2',
            '3',
            '4',
            '5',
            '6',
            '7',
            '8',
            '9',
            '.',
            '-',
            'Backspace',
            'Delete',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Tab',
            'Home',
            'End',
          ];
          if (!allowedKeys.includes(e.key)) {
            e.preventDefault();
          }
        }}
        onBlur={(e) => {
          const fieldValue = e.target.value.trim();
          let err: string | undefined = undefined;
          if (validate) {
            err = validate(fieldValue);
          } else if (required) {
            err = fieldValue === '' ? t('requiredField') : undefined;
          }
          setErrorMsg(err);
          onSetFieldValue?.(fieldValue);
        }}
        helperText={errorMsg}
        label={
          <>
            {label}
            {required && <span style={{ color: 'red', marginLeft: 2 }}>*</span>}
          </>
        }
        fullWidth
      />
    </Grid>
  );
};
export const FormGrid = ({
  formName,
  formDescription,
  fields,
  initialValues,
  values, // controlled values prop
  onChangeFieldValues,
}: IPlanFormGrid) => {
  const [currentFieldValues, setCurrentFieldValues] = useState<IFieldValues>({});

  // Use controlled values if provided, otherwise use internal state
  const effectiveValues = values ?? currentFieldValues;

  useEffect(() => {
    if (!values && Object.keys(currentFieldValues).length === 0) {
      const initialFieldValues: IFieldValues = {};
      fields.forEach((field) => {
        initialFieldValues[field.id] = field.value ?? initialValues?.[field.id] ?? '';
      });
      setCurrentFieldValues(initialFieldValues);
      onChangeFieldValues?.(initialFieldValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const setFieldValue = (fieldId: string, fieldValue: string | number) => {
    if (values) {
      // In controlled mode, just call the callback
      const fieldValues = { ...values, [fieldId]: fieldValue };
      onChangeFieldValues?.(fieldValues);
    } else {
      // In uncontrolled mode, update internal state
      const fieldValues = { ...currentFieldValues, [fieldId]: fieldValue };
      setCurrentFieldValues(fieldValues);
      onChangeFieldValues?.(fieldValues);
    }
  };

  return (
    <Box mt={formName ? 6 : 0} data-testid={formName}>
      {formName && (
        <Typography variant="h6" textTransform={'capitalize'}>
          <FormItemLabel label={formName} description={formDescription} />
        </Typography>
      )}
      <Grid container mt={formName ? 1 : 0} spacing={formName ? 2 : 1}>
        {fields.map((field) => (
          <FormGridItem
            key={field.id}
            onSetFieldValue={(value) => setFieldValue(field.id, value)}
            {...field}
            value={effectiveValues[field.id]}
          />
        ))}
      </Grid>
    </Box>
  );
};
